import PropTypes from "prop-types";
import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import ReactApexChart from "react-apexcharts";
import { useQuery } from "react-query";
import {
  Button,
  Dropdown,
  DropdownItem,
  DropdownMenu,
  DropdownToggle,
} from "reactstrap";
import { TIMELINE_CHART_HEIGHT_PX } from "./OrderManagementDashboard/chartConfig";
import HubMultiSelectFilter from "components/shared-components/HubMultiSelectFilter";
import orderService from "services/orderService";
import DashboardOrderTypeMultiFilter from "./OrderManagementDashboard/DashboardOrderTypeMultiFilter";
import {
  buildPendingCompletionOrdersListUrl,
  getDashboardHubFilterIds,
} from "./OrderManagementDashboard/orderManagementDashboardUtils";
import { useKpiPerformanceStats } from "./OrderManagementDashboard/useKpiPerformanceStats";

const MONTH_LABELS = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];

const BAR_COLORS = {
  total: "#06b6d4",
  success: "#c2410c",
  lineStroke: "#7C3AED",
  linePercentLabel: "#4C1D95",
  target: "#ff6b35",
};

const CHART_HEIGHT = TIMELINE_CHART_HEIGHT_PX;

// Max giá trị hai cột (hoàn tất / đạt KPI) để scale trục trái.
function resolveLeftAxisDataMax(monthlyData) {
  return monthlyData.reduce((maxNumber, monthItem) => {
    const totalCompleted = Number(monthItem.total_completed) || 0;
    const successCount = Number(monthItem.success_count) || 0;
    return Math.max(maxNumber, totalCompleted, successCount);
  }, 0);
}

// Headroom nhỏ + bước làm tròn gọn (tránh trục kéo quá cao).
function computeKpiLeftAxisBounds(dataMax) {
  const maxNumber = Math.max(0, Number(dataMax) || 0);
  if (maxNumber === 0) {
    return { max: 5, tickAmount: 5 };
  }
  const extraHeadroom = Math.min(5, Math.max(1, Math.ceil(maxNumber * 0.04)));
  const axisMax = maxNumber + extraHeadroom;
  const approxStep = axisMax / 5;
  const magnitude = 10 ** Math.floor(Math.log10(Math.max(approxStep, 1)));
  const normalized = approxStep / magnitude;
  let stepRatio = 1;
  if (normalized <= 1) stepRatio = 1;
  else if (normalized <= 2) stepRatio = 2;
  else if (normalized <= 5) stepRatio = 5;
  else stepRatio = 10;
  const step = stepRatio * magnitude;
  const roundedMax = Math.ceil(axisMax / step) * step;
  const tickAmount = Math.max(1, Math.round(roundedMax / step));
  return { max: roundedMax, tickAmount };
}

// Năm chọn từ 2022 đến năm sau hiện tại
function buildYearOptions() {
  const currentYear = new Date().getFullYear();
  const years = [];
  for (let y = 2022; y <= currentYear + 1; y++) {
    years.push(y);
  }
  return years.reverse();
}

const YEAR_OPTIONS = buildYearOptions();

// Chuỗi % locale vi-VN; ẩn khi 0
function formatKpiPercentDisplay(raw, options = {}) {
  const hideZero = options.hideZero !== false;
  if (raw == null || raw === "") {
    return "";
  }

  const valueNumber = Number(raw);
  if (!Number.isFinite(valueNumber)) {
    return "";
  }

  if (hideZero && valueNumber === 0) {
    return "";
  }

  const formattedNumber = valueNumber.toLocaleString("vi-VN", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 4,
  });

  return `${formattedNumber}%`;
}

// Parse % độ rộng cột Apex (mặc định 68).
function parseColumnWidthPercent(rawColumnWidth) {
  const parsed = parseInt(String(rawColumnWidth ?? "68"), 10);
  return Number.isFinite(parsed) ? parsed : 68;
}

// Nửa độ rộng một cột nhóm 
function computeGroupedBarHalfWidthPx(w) {
  const gridWidth = Number(w.globals.gridWidth) || 0;
  if (gridWidth <= 0) {
    return 0;
  }

  const dataPoints = Math.max(1, Number(w.globals.dataPoints) || 1);
  const tickPlacementOn = w.config.xaxis.tickPlacement === "on";
  const lineDivisor = Math.max(1, dataPoints - (tickPlacementOn ? 1 : 0));
  const xDivision = gridWidth / lineDivisor;

  const barSeriesCount = (w.config.series ?? []).filter(seriesItem => seriesItem.type === "bar").length;
  const seriesLen = Math.max(1, barSeriesCount);
  const columnWidthPct = parseColumnWidthPercent(w.config.plotOptions?.bar?.columnWidth);

  const barWidth = (xDivision / seriesLen) * (columnWidthPct / 100);

  return barWidth / 2;
}

// Đường line nối các % của đơn hoàn tất đạt KPI
function computeKpiLineSeriesTranslateX(w) {
  return computeGroupedBarHalfWidthPx(w);
}

// Overlay số đơn trên cột
function buildBarCountOverlayItems(chartRootEl, monthlyDataRows) {
  if (!chartRootEl || !Array.isArray(monthlyDataRows)) {
    return [];
  }

  const rootRect = chartRootEl.getBoundingClientRect();
  const barPlotRootEl = chartRootEl.querySelector(".apexcharts-bar-series");
  if (!barPlotRootEl) {
    return [];
  }

  let seriesRowGroups = Array.from(barPlotRootEl.querySelectorAll(":scope > .apexcharts-series"));
  if (!seriesRowGroups.length) {
    seriesRowGroups = Array.from(barPlotRootEl.children).filter(childEl => {
      const className = childEl.getAttribute("class") || "";

      return className.includes("apexcharts-series");
    });
  }

  const items = [];

  seriesRowGroups.forEach((seriesGroupEl, barSeriesLocalIndex) => {
    if (barSeriesLocalIndex > 1) {
      return;
    }

    let pathNodes = seriesGroupEl.querySelectorAll("path.apexcharts-bar-area");
    if (!pathNodes.length) {
      pathNodes = seriesGroupEl.querySelectorAll("path[class*='bar-area']");
    }

    pathNodes.forEach((pathEl, monthIndex) => {
      const rawValue =
        barSeriesLocalIndex === 0
          ? monthlyDataRows[monthIndex]?.total_completed
          : monthlyDataRows[monthIndex]?.success_count;
      const countNumber = Number(rawValue);
      if (!Number.isFinite(countNumber) || countNumber === 0) {
        return;
      }

      const pathRect = pathEl.getBoundingClientRect();
      if (pathRect.width <= 0 || pathRect.height <= 0) {
        return;
      }

      const centerX = pathRect.left + pathRect.width / 2 - rootRect.left;
      const centerY = pathRect.top + pathRect.height / 2 - rootRect.top;

      items.push({
        key: `kpi-bar-overlay-${barSeriesLocalIndex}-${monthIndex}`,
        leftPx: centerX,
        topPx: centerY,
        text: String(Math.round(countNumber)),
      });
    });
  });

  return items;
}

// Dịch SVG đường % để thẳng hàng với cột đạt KPI.
function alignKpiPercentLineWithSuccessBar(chart) {
  const rootEl = chart.el;
  if (!rootEl) {
    return;
  }

  const offsetPx = computeKpiLineSeriesTranslateX(chart.w);
  const lineSeriesEl = rootEl.querySelector(".apexcharts-line-series");
  if (!lineSeriesEl) {
    return;
  }

  lineSeriesEl.setAttribute("transform", `translate(${offsetPx}, 0)`);
}

// Biểu đồ cột
const KpiPerformanceBarChart = ({
  isParentLoading,
  kpiStatsBaseParams,
  hubSelectOptions,
  selectedHubIds,
  selectedOrderTypes,
  onHubFilterChange,
  onOrderTypeFilterChange,
}) => {
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [isYearDropdownOpen, setIsYearDropdownOpen] = useState(false);
  const [yearMenuWidthPx, setYearMenuWidthPx] = useState(
    /** @type {number | null} */(null)
  );
  const [barCountOverlayItems, setBarCountOverlayItems] = useState(
    /** @type {{ key: string, leftPx: number, topPx: number, text: string }[]} */([])
  );

  const chartWrapRef = useRef(/** @type {HTMLDivElement | null} */(null));
  const yearDropdownWrapRef = useRef(/** @type {HTMLDivElement | null} */(null));
  /** @type {React.MutableRefObject<{ el: HTMLElement | null } | null>} */
  const lastApexChartRef = useRef(null);

  const isKpiParamsReady = Boolean(kpiStatsBaseParams);

  const pendingCompletionHubIds = useMemo(
    () =>
      getDashboardHubFilterIds({
        selectedHubIds,
        hubSelectOptions,
      }),
    [selectedHubIds, hubSelectOptions]
  );

  const { monthlyData, targetSuccessPercent, isFetching, isLoading } = useKpiPerformanceStats({
    year: selectedYear,
    baseParams: kpiStatsBaseParams,
    enabled: isKpiParamsReady,
  });

  const { data: pendingCompletionCountResponse } = useQuery(
    ["delivered-pending-completion-count", pendingCompletionHubIds],
    () =>
      orderService.countDeliveredPendingCompletion({
        hub_ids: pendingCompletionHubIds,
      }),
    {
      enabled: Boolean(isKpiParamsReady && !isParentLoading && pendingCompletionHubIds),
      retry: 1,
      refetchOnWindowFocus: false,
    }
  );

  const deliveredPendingCompletionCount = useMemo(() => {
    const countValue = Number(pendingCompletionCountResponse?.data?.count);
    return Number.isFinite(countValue) ? countValue : 0;
  }, [pendingCompletionCountResponse]);

  const isChartLoading = isParentLoading || !isKpiParamsReady || isFetching || isLoading;

  const handleKpiYearSelect = useCallback(yearOption => {
    setSelectedYear(yearOption);
    setIsYearDropdownOpen(false);
  }, []);

  const handleOpenPendingCompletionOrders = useCallback(() => {
    const ordersUrl = buildPendingCompletionOrdersListUrl(pendingCompletionHubIds);
    window.open(ordersUrl, "_blank", "noopener,noreferrer")?.focus();
  }, [pendingCompletionHubIds]);

  useLayoutEffect(() => {
    const wrapEl = yearDropdownWrapRef.current;
    if (!wrapEl || typeof ResizeObserver === "undefined") {
      return undefined;
    }

    const resolveYearToggleEl = () =>
      wrapEl.querySelector("button.dropdown-toggle");

    const syncYearMenuWidth = () => {
      const toggleEl = resolveYearToggleEl();
      if (!toggleEl || !(toggleEl instanceof HTMLElement)) {
        return;
      }
      const widthNumber = toggleEl.offsetWidth;
      setYearMenuWidthPx(widthNumber > 0 ? widthNumber : null);
    };

    syncYearMenuWidth();

    const toggleElForObserve = resolveYearToggleEl();
    if (!toggleElForObserve || !(toggleElForObserve instanceof HTMLElement)) {
      return undefined;
    }

    const resizeObserver = new ResizeObserver(syncYearMenuWidth);
    resizeObserver.observe(toggleElForObserve);

    return () => {
      resizeObserver.disconnect();
    };
  }, [selectedYear]);

  const series = useMemo(() => [
    {
      name: "Tổng đơn hoàn tất",
      type: "bar",
      data: monthlyData.map(item => item.total_completed),
    },
    {
      name: "Tổng đơn hoàn tất đạt KPI",
      type: "bar",
      data: monthlyData.map(item => item.success_count),
    },
    {
      name: "% đạt KPI",
      type: "line",
      data: monthlyData.map(item => item.success_percent),
    },
  ], [monthlyData]);

  const leftAxisDataMax = useMemo(
    () => resolveLeftAxisDataMax(monthlyData),
    [monthlyData]
  );

  const leftAxisBounds = useMemo(
    () => computeKpiLeftAxisBounds(leftAxisDataMax),
    [leftAxisDataMax]
  );

  const syncBarCountOverlayFromChart = useCallback(chartContext => {
    lastApexChartRef.current = chartContext;
    const items = buildBarCountOverlayItems(chartContext?.el, monthlyData);
    setBarCountOverlayItems(items);
  }, [monthlyData]);

  const scheduleKpiChartSvgFixes = useCallback(chartContext => {
    alignKpiPercentLineWithSuccessBar(chartContext);
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        syncBarCountOverlayFromChart(chartContext);
      });
    });
    setTimeout(() => {
      syncBarCountOverlayFromChart(chartContext);
    }, 50);
  }, [syncBarCountOverlayFromChart]);

  const scheduleKpiChartSvgFixesRef = useRef(scheduleKpiChartSvgFixes);
  scheduleKpiChartSvgFixesRef.current = scheduleKpiChartSvgFixes;

  useEffect(() => {
    const chartInstance = lastApexChartRef.current;
    if (chartInstance && !isChartLoading) {
      syncBarCountOverlayFromChart(chartInstance);
    }
  }, [monthlyData, isChartLoading, syncBarCountOverlayFromChart]);

  useEffect(() => {
    const wrapEl = chartWrapRef.current;
    if (!wrapEl || typeof ResizeObserver === "undefined") {
      return undefined;
    }

    const resizeObserver = new ResizeObserver(() => {
      const chartInstance = lastApexChartRef.current;
      if (chartInstance) {
        syncBarCountOverlayFromChart(chartInstance);
      }
    });

    resizeObserver.observe(wrapEl);

    return () => {
      resizeObserver.disconnect();
    };
  }, [syncBarCountOverlayFromChart]);

  const chartOptions = useMemo(() => ({
    chart: {
      type: "bar",
      height: CHART_HEIGHT,
      foreColor: "#000000",
      stacked: false,
      toolbar: { show: false },
      zoom: { enabled: false },
      animations: { enabled: false },
      fontFamily: "inherit",
      events: {
        mounted(chartContext) {
          scheduleKpiChartSvgFixesRef.current(chartContext);
        },
        updated(chartContext) {
          scheduleKpiChartSvgFixesRef.current(chartContext);
        },
      },
    },
    plotOptions: {
      bar: {
        horizontal: false,
        columnWidth: "68%",
        borderRadius: 2,
      },
    },
    fill: {
      opacity: [1, 1, 1],
    },
    stroke: {
      width: [0, 0, 2.5],
      curve: ["straight", "straight", "straight"],
      dashArray: [0, 0, 0],
      colors: [BAR_COLORS.total, BAR_COLORS.success, BAR_COLORS.lineStroke],
    },
    colors: [BAR_COLORS.total, BAR_COLORS.success, BAR_COLORS.lineStroke],
    // Line không vẽ marker
    markers: {
      size: [0, 0, 0],
    },
    xaxis: {
      categories: MONTH_LABELS,
      tickPlacement: "between",
      labels: {
        trim: false,
        style: {
          fontSize: "12px",
          fontWeight: 400,
          colors: MONTH_LABELS.map(() => "#000000"),
        },
      },
      axisBorder: { show: true, color: "#000000" },
      axisTicks: { show: true, color: "#000000" },
    },
    // 3 cấu hình trục Y: trái hiển thị, trái ẩn, phải %.
    yaxis: [
      {
        min: 0,
        max: leftAxisBounds.max,
        tickAmount: leftAxisBounds.tickAmount,
        axisBorder: { show: true, color: "#000000" },
        axisTicks: { show: true, color: "#000000" },
        title: { text: "Số đơn", style: { fontSize: "11px", color: "#000000" } },
        labels: {
          formatter: val => Number.isInteger(val) ? val : Math.round(val),
          style: { fontSize: "12px", colors: ["#000000"] },
        },
      },
      {
        show: false,
        min: 0,
        max: leftAxisBounds.max,
        tickAmount: leftAxisBounds.tickAmount,
      },
      {
        opposite: true,
        min: 0,
        max: 100,
        tickAmount: 5,
        axisBorder: { show: true, color: "#000000" },
        axisTicks: { show: true, color: "#000000" },
        title: { text: "Tỉ lệ (%)", style: { fontSize: "11px", color: "#000000" } },
        labels: {
          formatter: val => formatKpiPercentDisplay(val, { hideZero: false }),
          style: { fontSize: "12px", colors: ["#000000"] },
        },
      },
    ],
    annotations: {
      yaxis: [
        {
          y: targetSuccessPercent,
          yAxisIndex: 2,
          borderColor: BAR_COLORS.target,
          borderWidth: 2,
          strokeDashArray: 5,
          label: {
            text: `Target: ${targetSuccessPercent}%`,
            position: "right",
            offsetX: -4,
            style: {
              color: "#fff",
              background: BAR_COLORS.target,
              fontSize: "11px",
              padding: { top: 2, bottom: 2, left: 6, right: 6 },
            },
          },
        },
      ],
    },
    dataLabels: {
      enabled: true,
      enabledOnSeries: [2],
      textAnchor: "middle",
      offsetX: 0,
      // offsetY đẩy nhãn % lên khỏi line
      offsetY: -14,
      background: {
        enabled: false,
      },
      dropShadow: {
        enabled: true,
        top: 1,
        left: 0,
        blur: 2,
        opacity: 0.75,
        color: "#ffffff",
      },
      formatter: value => formatKpiPercentDisplay(value, { hideZero: true }),
      style: {
        fontSize: "11px",
        fontWeight: 600,
        colors: [BAR_COLORS.total, BAR_COLORS.success, BAR_COLORS.linePercentLabel],
      },
    },
    tooltip: {
      shared: true,
      intersect: false,
      y: [
        { formatter: val => `${val} đơn` },
        { formatter: val => `${val} đơn` },
        { formatter: val => formatKpiPercentDisplay(val, { hideZero: false }) },
      ],
    },
    legend: {
      show: true,
      position: "top",
      horizontalAlign: "center",
      offsetY: 2,
      fontSize: "14px",
      fontWeight: 400,
      onItemHover: {
        highlightDataSeries: false,
      },
      // Chỉ tô màu vòng chú thích
      markers: {
        width: 10,
        height: 10,
        radius: 5,
        strokeWidth: 0,
        strokeColor: "transparent",
        offsetX: 0,
        offsetY: 0,
      },
      itemMargin: { horizontal: 14, vertical: 2 },
      labels: {
        colors: "#000000",
      },
    },
    grid: {
      borderColor: "#f1f1f1",
      strokeDashArray: 0,
      xaxis: { lines: { show: true } },
      yaxis: { lines: { show: false } },
    },
  }), [leftAxisBounds.max, leftAxisBounds.tickAmount, targetSuccessPercent]);

  return (
    <div className="card h-100 mb-0">
      <div className="card-body">
        <div className="dashboardFilterBar">
          <HubMultiSelectFilter
            hubSelectOptions={hubSelectOptions}
            selectedHubIds={selectedHubIds}
            onChange={onHubFilterChange}
          />
          <DashboardOrderTypeMultiFilter
            selectedOrderTypes={selectedOrderTypes}
            onChange={onOrderTypeFilterChange}
          />
          <div ref={yearDropdownWrapRef} className="kpi-year-dropdown">
            <Dropdown
              isOpen={isYearDropdownOpen}
              toggle={() => setIsYearDropdownOpen(prev => !prev)}
            >
              <DropdownToggle
                className="btn primary-btn text-overflow"
                caret
                id="kpi-year-dropdown"
              >
                Năm:&nbsp;{selectedYear}
              </DropdownToggle>
              <DropdownMenu
                style={
                  yearMenuWidthPx != null
                    ? { width: yearMenuWidthPx, minWidth: yearMenuWidthPx }
                    : undefined
                }
              >
                {YEAR_OPTIONS.map(yearOption => (
                  <DropdownItem
                    key={yearOption}
                    active={yearOption === selectedYear}
                    toggle={false}
                    onClick={() => handleKpiYearSelect(yearOption)}
                  >
                    {yearOption}
                  </DropdownItem>
                ))}
              </DropdownMenu>
            </Dropdown>
          </div>
          <Button
            type="button"
            className="btn primary-btn btn-label text-overflow"
            onClick={handleOpenPendingCompletionOrders}
            title="Mở danh sách đơn chưa hoàn tất"
          >
            <i className="bx bx-time-five label-icon" />
            Đơn chưa hoàn tất {`(${deliveredPendingCompletionCount})`}
          </Button>
        </div>

        <div className="mb-2">
          <h5 className="text-primary mb-0 fw-bold">
            Thống kê đơn hàng tổng quát
          </h5>
        </div>

        {isChartLoading ? (
          <div
            className="d-flex flex-column align-items-center justify-content-center"
            style={{ minHeight: `${CHART_HEIGHT + 24}px` }}
          >
            <div
              className="spinner-border text-primary"
              role="status"
              style={{ width: "2rem", height: "2rem" }}
            >
              <span className="visually-hidden">Đang tải dữ liệu thống kê...</span>
            </div>
            <span className="text-primary small mt-2">Đang tải dữ liệu thống kê...</span>
          </div>
        ) : (
          <div
            ref={chartWrapRef}
            className="kpiPerformanceChartWrap position-relative"
          >
            <ReactApexChart
              options={/** @type {any} */ (chartOptions)}
              series={series}
              type="bar"
              height={CHART_HEIGHT}
            />
            <div className="kpi-chart-bar-count-overlay" aria-hidden>
              {barCountOverlayItems.map(item => (
                <span
                  key={item.key}
                  className="kpi-chart-bar-count-label"
                  style={{
                    left: item.leftPx,
                    top: item.topPx,
                  }}
                >
                  {item.text}
                </span>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

KpiPerformanceBarChart.propTypes = {
  isParentLoading: PropTypes.bool,
  kpiStatsBaseParams: PropTypes.oneOfType([
    PropTypes.shape({
      hub_id: PropTypes.string.isRequired,
      order_type: PropTypes.string,
    }),
    PropTypes.oneOf([null]),
  ]),
  hubSelectOptions: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
    })
  ).isRequired,
  selectedHubIds: PropTypes.arrayOf(PropTypes.string).isRequired,
  selectedOrderTypes: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.string),
    PropTypes.oneOf([null]),
  ]),
  onHubFilterChange: PropTypes.func.isRequired,
  onOrderTypeFilterChange: PropTypes.func.isRequired,
};

KpiPerformanceBarChart.defaultProps = {
  isParentLoading: false,
  kpiStatsBaseParams: null,
};

export default KpiPerformanceBarChart;
